import { handleApiError, handleApiResponse } from "@/methods/api";
import ApiVerified from "@/services/apiVerified";
import { ApiResponse, PaginatedResponse } from "@/types/global";
import {
  CreateLiveClassInputs,
  JoinLiveClassDisplay,
  LiveClassDisplay,
  LiveClassLogsDisplay,
  LiveClassLogsInputs,
} from "@/types/liveClass";

export default class LiveClassApi extends ApiVerified {
  async getLiveClassesForAffiliation(
    affiliationId: number,
    category: number,
    pageNumber: number,
    pageSize: number,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<PaginatedResponse<LiveClassDisplay>>(
        `live-class/get-live-classes-for-affiliation`,
        {
          params: {
            affiliationId: affiliationId,
            category: category,
            pageNumber: pageNumber,
            pageSize: pageSize,
          },
        },
      );
      return handleApiResponse(response, true);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getLiveClassesForFaculty(
    userId: number,
    category: number,
    pageNumber: number,
    pageSize: number,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<PaginatedResponse<LiveClassDisplay>>(
        `live-class/get-live-classes-for-faculty`,
        {
          params: {
            userId: userId,
            category: category,
            pageNumber: pageNumber,
            pageSize: pageSize,
          },
        },
      );
      return handleApiResponse(response, true);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getLiveClassesForStudent(
    userId: number,
    userName: string,
    category: number,
    pageNumber: number,
    pageSize: number,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<PaginatedResponse<LiveClassDisplay>>(
        `live-class/get-live-classes-for-student`,
        {
          params: {
            userId: userId,
            userName: userName,
            category: category,
            pageNumber: pageNumber,
            pageSize: pageSize,
          },
        },
      );
      return handleApiResponse(response, true);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async rescheduleLiveClass(
    liveClassId: number,
    startTime: string,
    duration: number,
    includeBasicZoom: boolean,
    allFutureEvents: boolean,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post(`live-class/reschedule-live-class`, {
        LiveClassId: liveClassId,
        StartTime: startTime,
        Duration: duration,
        IncludeBasicZoom: includeBasicZoom,
        AllFutureEvents: allFutureEvents,
      });
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async modifyLiveClassSubject(
    liveClassId: number,
    subjectId: number,
    allFutureEvents: boolean,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(
        `live-class/modify-live-class-subject`,
        { LiveClassId: liveClassId, SubjectId: subjectId, AllFutureEvents: allFutureEvents },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async modifyLiveClassFaculty(
    liveClassId: number,
    facultyId: number,
    allFutureEvents: boolean,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(
        `live-class/modify-live-class-faculty`,
        {
          LiveClassId: liveClassId,
          FacultyId: facultyId,
          AllFutureEvents: allFutureEvents,
        },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async startLiveClass(liveClassId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(`live-class/start-live-class`, {
        LiveClassId: liveClassId,
      });
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async endLiveClass(liveClassId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(`live-class/end-live-class`, {
        LiveClassId: liveClassId,
      });
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async updateZoomModeratorUrl(liveClassId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(
        `live-class/update-zoom-moderator-url`,
        { LiveClassId: liveClassId },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async addClassLogs(classLogsInputs: LiveClassLogsInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(
        `live-class/add-class-logs`,
        classLogsInputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getClassLogs(liveClassId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<LiveClassLogsDisplay>(
        `live-class/get-class-logs`,
        { params: { liveClassId: liveClassId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async cancelLiveClass(liveClassId: number, allFutureEvents: boolean): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(`live-class/cancel-live-class`, {
        LiveClassId: liveClassId,
        AllFutureEvents: allFutureEvents,
      });
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async createLiveClass(liveClassInputs: CreateLiveClassInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(
        `live-class/create-live-class`,
        liveClassInputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // Marks the caller (taken from the JWT) present on a running live class and returns the
  // meeting URL to open. No request body — there is no way, and no field, to mark anyone else.
  // Idempotent for attendance: a repeat call only advances JoinCount, so a retry is always safe.
  async joinLiveClass(liveClassId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<JoinLiveClassDisplay>(
        `live-class/${liveClassId}/join`,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }
}
